/**
 * PatientInvoicesList — Invoices sub-tab of Patient Billing (PROD-733).
 *
 * Flutter ref: `patient_invoices_list_screen.dart`. The full Flutter table
 * has 12 columns; PR A renders 7. Filter widgets defer to PR B; "Add
 * Payment" + per-row actions defer to PR C.
 *
 * Wire ships a `PagedResults<InvoiceMainModel>` envelope (swagger says
 * flat array but the live API ships the envelope — verified 2026-05-15).
 * The pager renders Page X of Y using `totalNumberOfPages`.
 */

import { useState } from 'react';
import { StatusChip, type StatusChipProps } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { Avatar } from '@scripsteam/scrips-signal-ds';
import { usePatientInvoices } from '@/patients/services/use-patient-invoices';
import {
  BillingListFilterBar,
  EMPTY_BILLING_FILTER,
  type BillingFilterValue,
} from './billing-list-filter-bar';
import { BillingPager } from './billing-pager';
import { PatientInvoiceRowActions } from './patient-invoice-row-actions';
import type { InvoiceMainModel } from '@/shared/types/billing';

import { formatMoney } from '@/shared/lib/money';
import { V2_BODY_EMPHASIS, V2_CARD_SURFACE, v2Look } from '@/shared/ui/design-v2';
// Verbatim Flutter `app_constants.dart` values (magic-string rule):
// paymentTypeSelfPay/Insurance/Corporate + patient_payment_status_*.
const SPONSOR_OPTIONS = [
  { value: 'Self-pay', label: 'Self Pay' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Corporate', label: 'Corporate' },
];
const PAYMENT_STATUS_OPTIONS = [
  { value: 'N/A', label: 'N/A' },
  { value: 'Paid in Full', label: 'Paid in Full' },
  { value: 'Partially paid', label: 'Partially paid' },
  { value: 'Not Paid', label: 'Not Paid' },
];

export interface PatientInvoicesListProps {
  patientId: string | null | undefined;
}

export function PatientInvoicesList({ patientId }: PatientInvoicesListProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<BillingFilterValue>(
    EMPTY_BILLING_FILTER,
  );

  const onFilters = (next: BillingFilterValue) => {
    setPage(1);
    setFilters(next);
  };

  const { data, isFetching } = usePatientInvoices({
    patientId,
    pageNumber: page,
    pageSize,
    searchQuery: filters.search || undefined,
    startDate: filters.startDate,
    endDate: filters.endDate,
    sponsorType: filters.facets.sponsorType?.length
      ? filters.facets.sponsorType
      : null,
    patientPaymentStatus: filters.facets.patientPaymentStatus?.length
      ? filters.facets.patientPaymentStatus
      : null,
  });

  const rows = data?.results ?? [];
  const totalPages = data?.totalNumberOfPages ?? 1;
  const totalRecords = data?.totalNumberOfRecords ?? 0;
  const hasActiveFilters =
    !!filters.search ||
    !!filters.startDate ||
    Object.values(filters.facets).some((v) => v.length > 0);

  const filterBar = (
    <BillingListFilterBar
      searchPlaceholder="Search by doctor, invoice #"
      facetGroups={[
        { key: 'sponsorType', title: 'Sponsor', options: SPONSOR_OPTIONS },
        {
          key: 'patientPaymentStatus',
          title: 'Payment Status',
          options: PAYMENT_STATUS_OPTIONS,
        },
      ]}
      value={filters}
      onChange={onFilters}
    />
  );

  if (isFetching && rows.length === 0) {
    return (
      <div className="space-y-3">
        {filterBar}
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        {filterBar}
        <div className="rounded-lg border border-dashed border-border-default p-8 text-center">
          <p className="text-sm text-text-muted">
            {hasActiveFilters
              ? 'No invoices match these filters.'
              : 'No invoices yet for this patient.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filterBar}
      <div className={v2Look('overflow-x-auto scrollbar-hide rounded-lg border border-border-default', V2_CARD_SURFACE)}>
        <table className="w-full min-w-275 text-sm">
          <thead className="bg-grey-100">
            <tr className="text-left text-text-muted">
              <Th>Encounter date</Th>
              <Th>Invoice #</Th>
              <Th>Invoice date</Th>
              <Th>Doctor</Th>
              <Th>Sponsor type</Th>
              <Th>Sponsor</Th>
              <Th align="right">Total net</Th>
              <Th align="right">Sponsor amount</Th>
              <Th align="right">Patient amount</Th>
              <Th align="right">Outstanding</Th>
              <Th>Status</Th>
              <Th>Note</Th>
              <Th align="right"> </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.id ?? row.invoiceNumber ?? `row-${i}`}
                className="border-t border-border-default hover:bg-grey-100/50"
              >
                <Td>{formatDate(row.encounterDate)}</Td>
                <Td>
                  {/* PROD-2743 · switch ON: a record number is a value, not a link: body-emphasis in
                      text-primary, not brand ink. OFF = today. */}
                  <span className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
                    {row.invoiceNumber ?? '—'}
                  </span>
                </Td>
                <Td>{formatDate(row.invoiceDate)}</Td>
                <Td>
                  <DoctorCell row={row} />
                </Td>
                <Td>{row.sponsorType ?? '—'}</Td>
                <Td>{formatSponsor(row)}</Td>
                <Td align="right">AED {formatMoney(row.netTotal ?? 0)}</Td>
                <Td align="right">
                  AED {formatMoney(row.sponsorAmountTotal ?? 0)}
                </Td>
                <Td align="right">
                  AED {formatMoney(row.patientPayable ?? 0)}
                </Td>
                <Td align="right">
                  AED {formatMoney(row.patientOutstanding ?? 0)}
                </Td>
                <Td>
                  <InvoiceStatusCell
                    status={
                      row.invoiceStatus ?? row.patientPaymentStatus ?? null
                    }
                  />
                </Td>
                <Td>
                  <span
                    className="line-clamp-1 inline-block max-w-40 text-text-secondary"
                    title={row.note ?? undefined}
                  >
                    {row.note || '—'}
                  </span>
                </Td>
                <Td align="right">
                  <PatientInvoiceRowActions
                    row={row}
                    patientId={patientId as string}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BillingPager
        page={page}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        isFetching={isFetching}
        noun="invoices"
        onPage={setPage}
        onPageSize={(n) => {
          setPage(1);
          setPageSize(n);
        }}
      />
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`px-3 py-2 text-xs font-bold uppercase tracking-wide ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <td
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </td>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function formatProvider(row: InvoiceMainModel): string {
  if (row.providerName) return row.providerName;
  const p = row.provider;
  if (!p) return '—';
  return p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ') || '—';
}

/** Payer name; "Self Pay" for self-pay invoices (Flutter parity). */
function formatSponsor(row: InvoiceMainModel): string {
  if ((row.sponsorType ?? '').toLowerCase() === 'self-pay') return 'Self Pay';
  return row.sponsor?.name || '—';
}

export function DoctorCell({ row }: { row: InvoiceMainModel }) {
  const name = formatProvider(row);
  // The 2xs step is the DS 16px box with an 8px initials font — the exact geometry this
  // row rendered before the migration. Initials stay local: this cell shows the
  // FIRST CHARACTER only, where the DS's computeInitials would give first+last.
  return (
    <div className="flex items-center gap-2">
      <Avatar
        size="2xs"
        src={row.provider?.imageUrl || undefined}
        name={name === '—' ? undefined : name}
        initials={name === '—' ? '?' : name.slice(0, 1)}
      />
      <span className="truncate">{name}</span>
    </div>
  );
}

/** Exported for its test. */
export function InvoiceStatusCell({ status }: { status: string | null }) {
  if (!status) return <span className="text-text-hint">—</span>;
  const lower = status.toLowerCase();
  // Default to the Signal StatusChip (tone="soft" = ramp/100 bg + ramp/700
  // text) rather than hand-binding the tint classes (CLAUDE.md rule).
  // Meaning first: "Not Paid" and "Partially paid" contain "paid" but are not settled, so they are not
  // green (a colour every row shares carries no meaning; green only for what is done).
  const semantic: NonNullable<StatusChipProps['semantic']> =
    lower.includes('cancel') || lower.includes('void')
      ? 'error'
      : lower.includes('not paid') || lower.includes('unpaid') || lower.includes('partial')
        ? 'warning'
        : lower.includes('paid')
          ? 'success'
          : lower.includes('draft') || lower.includes('pending')
            ? 'warning'
            : lower === 'n/a'
              ? 'neutral'
              : 'info';
  return (
    <StatusChip semantic={semantic} tone="soft" data-chip-look={lower.includes('draft') ? 'draft' : undefined}>
      {status}
    </StatusChip>
  );
}
